import type { FileId } from "@inrev/common";
import { Option } from "@inrev/core";
import { useColumnWidth, useColumnWidthSource } from "@portal/hooks/useColumnWidth";
import type { PDFDocumentProxy } from "pdfjs-dist";
import {
	type HTMLAttributes,
	type KeyboardEvent,
	type Ref,
	useCallback,
	useEffect,
	useEffectEvent,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib/utils";
import { panelScrollRegionProps } from "../floating-panel";
import { type PDFViewerControl, useDocumentUrl } from "./documentSource";
import { FindBar } from "./FindBar";
import { HighlightLayer } from "./HighlightLayer";
import type { Highlight } from "./highlights";
import { shortcutCommand } from "./shortcuts";
import { fileIdFromSignedUrl, toFileTracking } from "./tracking";
import { useDocumentViewer } from "./useDocumentViewer";
import { useFieldSelection } from "./useFieldSelection";
import { useViewerActions } from "./useViewerActions";
import { ViewerActionError, ViewerNoLocation, ViewerOverlay } from "./ViewerStates";
import { ViewerToolbar, type ViewerToolbarVariant } from "./ViewerToolbar";
import { toViewerIds, ViewerIdsContext } from "./viewerIds";
import type { ViewerSession, ZoomMode } from "./viewerSession";
import { type DocumentViewerStatus, STATUS_BY_VIEW, viewStateOf } from "./viewState";
import "./documentViewer.css";

const NO_HIGHLIGHTS: readonly Highlight[] = [];
/** Below this width an embedded viewer moves what doesn't fit into More. */
const COMPACT_WIDTH_PX = 560;

export type DocumentViewerHandle = {
	/** Scrolls to the field's first highlight and pulses it, even when it is already selected. */
	scrollToField: (fieldId: string) => void;
	/** Moves to a one-based page, clamped to the document. Does nothing until it has loaded. */
	goToPage: (page: number) => void;
	/** Does nothing until the document has loaded. */
	setZoom: (zoom: ZoomMode) => void;
};

export type DocumentViewerProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
	ref?: Ref<DocumentViewerHandle>;
	/** The root element, for callers that size or measure it. */
	rootRef?: Ref<HTMLDivElement>;
	/** None renders the no-document state in place of the pages. */
	control: Option<PDFViewerControl>;
	/** Names the document for screen readers. */
	fileName?: string;
	/** Boxes in PDF points. The viewer knows nothing about where they came from. */
	highlights?: readonly Highlight[];
	/** The selected field. Selecting a new one scrolls to it when it isn't fully visible. */
	activeFieldId?: Option<string>;
	/** A field to preview, e.g. while its row is hovered. Never scrolls. */
	hoveredFieldId?: Option<string>;
	showAllHighlights?: boolean;
	/** Makes highlights buttons that select their field. */
	onFieldSelect?: (fieldId: string) => void;
	initialSession?: Partial<ViewerSession>;
	/** Fires once per loaded document, e.g. to index its text. */
	onDocumentLoad?: (pdf: PDFDocumentProxy) => void;
	/** `unavailable` covers no document, a missing or failed file, and one pdf.js can't read. */
	onStatusChange?: (status: DocumentViewerStatus) => void;
	/** Shown in place of the generic error when the URL fetch 404s; only the caller knows what that means. */
	notFoundMessage?: string;
	/** Names the surface, e.g. `account-documents-attachment-viewer`; every control's id starts with it. */
	trackingId: string;
	testId?: string;
};

/**
 * A pdf.js viewer with page navigation, zoom, find, rotate, download, print and field
 * highlights. It fetches the file's signed URL through `control`, owns every loading and error
 * state inside its viewport, and only touches pdf.js through `useDocumentViewer`. Size it with
 * `className`.
 */
export function DocumentViewer({
	control,
	fileName,
	highlights = NO_HIGHLIGHTS,
	activeFieldId,
	hoveredFieldId,
	showAllHighlights = false,
	onFieldSelect,
	initialSession,
	onDocumentLoad,
	onStatusChange,
	notFoundMessage,
	className,
	trackingId,
	testId = "document-viewer",
	onKeyDown,
	ref,
	rootRef,
	...rest
}: DocumentViewerProps) {
	const source = useDocumentUrl(control);
	const fileId: Option<FileId> = control?.fileId ?? Option.flatMap(source.url, fileIdFromSignedUrl);
	const fileTracking = useMemo(() => toFileTracking(fileId), [fileId]);
	const ids = useMemo(
		() => toViewerIds(testId, trackingId, fileTracking),
		[testId, trackingId, fileTracking],
	);
	const viewer = useDocumentViewer({
		url: source.url,
		initialSession,
		onDocumentLoad,
		refreshUrl: source.refresh,
	});
	const ready = viewer.load.status === "ready";
	const { goToPage, setZoom, stepZoom, closeFind: clearFind } = viewer;
	const actions = useViewerActions(control, viewer.print);
	const selection = useFieldSelection({
		highlights,
		activeFieldId,
		ready,
		scrollToRects: viewer.scrollToRects,
	});
	const [findOpen, setFindOpen] = useState(false);
	const [findFocusKey, setFindFocusKey] = useState(0);
	const findButtonRef = useRef<HTMLButtonElement>(null);
	const viewport = useColumnWidthSource();
	const narrow = useColumnWidth(
		viewport,
		(width) => width !== undefined && width > 0 && width < COMPACT_WIDTH_PX,
	);

	const variant: ViewerToolbarVariant = narrow ? "compact" : "embedded";

	useImperativeHandle(ref, () => ({ scrollToField: selection.scrollToField, goToPage, setZoom }), [
		selection.scrollToField,
		goToPage,
		setZoom,
	]);

	const reportStatus = useEffectEvent((next: DocumentViewerStatus) => onStatusChange?.(next));
	const view = viewStateOf({ control, source, load: viewer.load, notFoundMessage });
	const status = STATUS_BY_VIEW[view.kind];
	useEffect(() => {
		reportStatus(status);
	}, [status]);

	const openFind = useCallback(() => {
		setFindOpen(true);
		setFindFocusKey((current) => current + 1);
	}, []);
	// A replaced document clears the search, and a find bar reopening on it would take focus.
	useEffect(() => {
		if (!ready) setFindOpen(false);
	}, [ready]);
	const closeFind = useCallback(() => {
		setFindOpen(false);
		clearFind();
		findButtonRef.current?.focus();
	}, [clearFind]);

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		onKeyDown?.(event);
		if (event.defaultPrevented || !ready) return;
		const run = shortcutCommand(event, {
			openFind,
			stepZoom,
			setZoom,
			goToPage,
			page: viewer.page,
			pageCount: viewer.pageCount,
		});
		if (Option.isNone(run)) return;
		event.preventDefault();
		run();
	};

	const { activeHighlight } = selection;
	const missingLocation = ready && Option.isSome(activeFieldId) && Option.isNone(activeHighlight);
	const announcement = Option.mapOrElse(
		activeHighlight,
		({ label, page }) => (label ? `${label}, page ${page}` : `Highlighted on page ${page}`),
		"",
	);

	return (
		<ViewerIdsContext.Provider value={ids}>
			<div
				ref={rootRef}
				className={cn(
					"relative flex min-h-0 flex-col overflow-hidden bg-surface-neutral",
					className,
				)}
				data-testid={testId}
				data-amp-block
				onKeyDown={handleKeyDown}
				{...rest}
			>
				<ViewerToolbar
					variant={variant}
					ready={ready}
					page={viewer.page}
					pageCount={viewer.pageCount}
					scale={viewer.scale}
					zoom={viewer.zoom}
					findOpen={findOpen}
					canDownload={actions.canDownload}
					downloading={actions.downloading}
					printing={actions.printing}
					onPageChange={goToPage}
					onZoomChange={setZoom}
					onZoomStep={stepZoom}
					onToggleFind={findOpen ? closeFind : openFind}
					onRotate={viewer.rotate}
					onDownload={actions.download}
					onPrint={actions.print}
					findButtonRef={findButtonRef}
				/>
				{findOpen && ready && (
					<FindBar
						status={viewer.find}
						onSearch={viewer.runFind}
						onClose={closeFind}
						focusKey={findFocusKey}
					/>
				)}
				{Option.isSome(actions.error) && (
					<ViewerActionError message={actions.error} onDismiss={actions.dismissError} />
				)}
				{missingLocation && <ViewerNoLocation />}
				<div ref={viewport.ref} className="relative min-h-0 flex-1">
					<div
						ref={viewer.containerRef}
						className="documentViewerScroll absolute inset-0 overflow-auto"
						tabIndex={0}
						role="region"
						aria-label={fileName ? `Document: ${fileName}` : "Document"}
						data-testid={ids.testId("scroll")}
						data-amp-mask
						{...panelScrollRegionProps}
					>
						<div ref={viewer.pagesRef} className="pdfViewer" />
					</div>
					<ViewerOverlay
						view={view}
						onRetryUrl={() => void source.refresh()}
						retryingUrl={source.isRefreshing}
						onRetryLoad={viewer.retry}
						onSubmitPassword={viewer.submitPassword}
						onDownload={actions.download}
						downloading={actions.downloading}
					/>
				</div>
				{highlights.length > 0 && (
					<HighlightLayer
						layers={viewer.layers}
						highlights={highlights}
						activeFieldId={activeFieldId}
						hoveredFieldId={hoveredFieldId}
						showAll={showAllHighlights}
						pulse={selection.pulse}
						onSelect={onFieldSelect}
					/>
				)}
				<div aria-live="polite" className="sr-only" data-testid={ids.testId("announcement")}>
					{announcement}
				</div>
			</div>
		</ViewerIdsContext.Provider>
	);
}
DocumentViewer.displayName = "DocumentViewer";
